import { useSelector } from 'react-redux';

import { messages } from './messages';
import { usePlanFeatureDisplayMode } from './use-plan-feature-display-mode';
import FeatureFlagDeclarations from '../feature-flags/feature-flag-declarations';
import useFeatureFlag from '../feature-flags/hooks/use-feature-flag';
import { useIsSellerFeatureEnabled } from '../hooks/use-is-seller-feature-enabled';
import Global from '../store/global-selectors';

/** @typedef {import('../constants/features-type').FeatureCode} FeatureCode */
/** @typedef {import('../prop-types/intl-types').MessageDescriptor} MessageDescriptor */

const Enabled = 'Enabled';
const Disabled = 'Disabled';
/** @typedef {'Enabled' | 'Disabled'} FeatureAccessValueType */

/** @type {Record<FeatureAccessValueType, FeatureAccessValueType>} */
export const FeatureAccessValue = {
  Enabled,
  Disabled,
};

export const EnabledResponse = { isFeatureEnabled: true };
export const DisabledResponse = {
  isFeatureEnabled: false,
  disabledMessage: messages.featureNotAvailableOnCurrentPlan,
};

const DefaultResponseMap = {
  [Enabled]: EnabledResponse,
  [Disabled]: DisabledResponse,
};

/**
 * @param {FeatureCode} featureCode
 * @param {FeatureAccessValueType} [defaultFeatureAccessValue] default is 'Enabled'
 * @returns {{ isFeatureEnabled: boolean, disabledMessage?: MessageDescriptor, shouldHide?: boolean }}
 */
export const usePlanFeatureGate = (featureCode, defaultFeatureAccessValue = Enabled) => {
  const isSystemAdmin = useSelector(Global.selectUserisSysAdmin);
  const isFeatureFlagEnabled = useFeatureFlag(FeatureFlagDeclarations.enable2025Q2PlanFeatureGates);
  const isSellerFeatureEnabled = useIsSellerFeatureEnabled(featureCode);
  const featureGatedDisplayMode = usePlanFeatureDisplayMode(featureCode);

  if (isSystemAdmin) {
    return EnabledResponse;
  }

  if (!isFeatureFlagEnabled) {
    return DefaultResponseMap[defaultFeatureAccessValue];
  }

  if (isSellerFeatureEnabled) {
    return EnabledResponse;
  }

  return { ...DisabledResponse, ...featureGatedDisplayMode };
};
